import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { readInstanceIdsParam } from '../../../hooks/useChatUrlInstanceIds';
import type { Control } from '../../types/controls';
import { instanceControlKeyOf } from '../templateControls';
import { type AppliedDashboardFilters, PREFILL_PARAM, readControlSelections, TIME_RANGE_PARAM } from './filterParams';

/** `controls` folds the instance scope back under its control's key, the single map the API
 * expects. Memoized whole: callers put it in effect deps and query keys. */
export const useAppliedDashboardFilters = (controls: Control[] = []): AppliedDashboardFilters => {
  const [searchParams] = useSearchParams();
  const instanceControlKey = instanceControlKeyOf(controls);
  const scopedInstanceIds = useMemo(() => readInstanceIdsParam(searchParams), [searchParams]);
  const selectionsByControlKey = useMemo(() => {
    const fromParams = readControlSelections(searchParams);
    return instanceControlKey && scopedInstanceIds.length > 0
      ? { ...fromParams, [instanceControlKey]: scopedInstanceIds }
      : fromParams;
  }, [searchParams, instanceControlKey, scopedInstanceIds]);
  const timeRangeOverride = searchParams.get(TIME_RANGE_PARAM) ?? undefined;
  const isApplied = !searchParams.has(PREFILL_PARAM);
  return useMemo(
    () => ({ selectionsByControlKey, scopedInstanceIds, timeRangeOverride, isApplied }),
    [selectionsByControlKey, scopedInstanceIds, timeRangeOverride, isApplied],
  );
};
